import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CanvasComponent } from '@/types';

export default function Preview() {
  const [searchParams] = useSearchParams();
  const [components, setComponents] = useState<CanvasComponent[]>([]);
  const [styles, setStyles] = useState('');
  const [scripts, setScripts] = useState('');

  useEffect(() => {
    const componentsData = searchParams.get('components');
    if (componentsData) {
      try {
        const decodedComponents = JSON.parse(decodeURIComponent(componentsData));
        setComponents(decodedComponents);

        // Generate CSS
        const css = generateCSS(decodedComponents);
        setStyles(css);

        // Generate JavaScript
        const js = generateJavaScript(decodedComponents);
        setScripts(js);
      } catch (error) {
        console.error('Failed to parse components data:', error);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    // Execute component scripts
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.innerHTML = scripts;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [scripts]);

  return (
    <div className="preview-container relative w-screen h-screen bg-background">
      <style>{styles}</style>
      {components.map((component) => (
        <div
          key={component.id}
          id={component.properties.name}
          className={`absolute ${component.type}-component`}
          style={{
            left: `${component.position.x}px`,
            top: `${component.position.y}px`,
            width: `${component.size.width}px`,
            height: `${component.size.height}px`,
            zIndex: component.properties.zIndex || 0,
            opacity: (component.properties.opacity ?? 100) / 100,
          }}
        >
          {renderComponent(component)}
        </div>
      ))}
    </div>
  );
}

function renderComponent(component: CanvasComponent) {
  const baseStyle = {
    width: '100%',
    height: '100%',
    ...(component.type === 'text' && {
      fontWeight: component.properties.fontStyles?.bold ? 'bold' : 'normal',
      fontStyle: component.properties.fontStyles?.italic ? 'italic' : 'normal',
      textDecoration: component.properties.fontStyles?.underline ? 'underline' : 'none',
      fontSize: `${component.properties.fontSize || 16}px`,
      color: component.properties.fontColor || 'inherit',
    }),
  };

  switch (component.type) {
    case 'button':
      return (
        <button className="w-full h-full px-4 py-2 bg-primary text-primary-foreground rounded">
          {component.properties.text}
        </button>
      );
    case 'input':
      return (
        <input
          type="text"
          className="w-full h-full px-4 py-2 border rounded"
          placeholder={component.properties.text}
        />
      );
    case 'text':
      return <p style={baseStyle}>{component.properties.text}</p>;
    case 'image':
      return (
        <img
          src={component.properties.src || 'https://via.placeholder.com/150'}
          alt={component.properties.text}
          className="w-full h-full object-cover"
        />
      );
    case 'card':
      return (
        <div className="w-full h-full p-4 bg-card text-card-foreground rounded-lg shadow">
          {component.properties.text}
        </div>
      );
    case 'list':
      return (
        <ul className="w-full h-full list-disc pl-4">
          <li>{component.properties.text}</li>
        </ul>
      );
    case 'panel':
      return (
        <div className="w-full h-full bg-background border rounded p-4">
          {component.properties.text}
        </div>
      );
    default:
      return null;
  }
}

function generateCSS(components: CanvasComponent[]): string {
  return `
    .preview-container {
      position: relative;
      width: 100vw;
      height: 100vh;
      overflow: hidden;
    }
    ${components.map((component) => `
      #${component.properties.name} {
        position: absolute;
        left: ${component.position.x}px;
        top: ${component.position.y}px;
        width: ${component.size.width}px;
        height: ${component.size.height}px;
        z-index: ${component.properties.zIndex || 0};
        opacity: ${(component.properties.opacity ?? 100) / 100};
        ${component.type === 'text' ? `
          font-weight: ${component.properties.fontStyles?.bold ? 'bold' : 'normal'};
          font-style: ${component.properties.fontStyles?.italic ? 'italic' : 'normal'};
          text-decoration: ${component.properties.fontStyles?.underline ? 'underline' : 'none'};
          font-size: ${component.properties.fontSize || 16}px;
          color: ${component.properties.fontColor || 'inherit'};
        ` : ''}
      }
    `).join('\n')}
  `;
}

function generateJavaScript(components: CanvasComponent[]): string {
  return components.map((component) => {
    const elementId = component.properties.name;
    let script = '';

    if (component.events.onClick) {
      script += `
        document.getElementById('${elementId}').addEventListener('click', function() {
          ${component.events.onClick}
        });
      `;
    }

    if (component.events.onHover) {
      script += `
        document.getElementById('${elementId}').addEventListener('mouseenter', function() {
          ${component.events.onHover}
        });
      `;
    }

    if (component.events.onDrag) {
      script += `
        document.getElementById('${elementId}').addEventListener('dragstart', function() {
          ${component.events.onDrag}
        });
      `;
    }

    return script;
  }).join('\n');
}